import { redirect } from '@tanstack/react-router';
import { isEmpty, pickBy } from 'remeda';
import { type z } from 'zod';

import { getContentLocale, getSavedFilters } from '@@scripts/store/settings/useSettingsStore';

import { type SearchNamespace } from './types';

type BeforeLoadOptions = {
    cause: 'enter' | 'stay' | 'preload';
    location: { search: object };
    search: Record<string, unknown>;
};

/**
 * For a list route's `beforeLoad`: on entering the list, redirects to the same URL with the
 * filters the user saved that the URL lacks. The URL's own values win. Only the filters of the
 * list's schema are restored, since several lists share a namespace.
 */
export const restoreSavedFilters = (
    { cause, location, search }: BeforeLoadOptions,
    schema: z.ZodObject,
    namespace: SearchNamespace,
) => {
    const saved = getSavedFilters(getContentLocale(), namespace);
    const listKeys = new Set<string>(schema.keyof().options);
    // Missing from the URL, where `search` holds the schema's default in their place, or dropped
    // by the schema, such as the single value of an array in an old link
    const missingFilters = pickBy(
        saved,
        (_value, key) =>
            listKeys.has(key) && (!(key in location.search) || search[key] === undefined),
    );

    // The redirect enters the list again, so only a missing filter may redirect
    if (cause === 'enter' && !isEmpty(missingFilters)) {
        throw redirect({ to: '.', search: { ...search, ...missingFilters }, replace: true });
    }
};
